/**
 * PageTreeContextMenu — Right-click context menu for page tree nodes.
 * Uses Radix UI ContextMenu primitive styled with Tailwind CSS.
 *
 * Provides discoverable access to page actions: Rename, New Child, Archive/Delete.
 */

import React from 'react';
import * as ContextMenu from '@radix-ui/react-context-menu';

interface PageTreeContextMenuProps {
  readonly children: React.ReactNode;
  readonly onRename: () => void;
  readonly onNewChild: () => void;
  readonly onArchive: () => void;
  readonly pageTitle: string;
}

export function PageTreeContextMenu({
  children,
  onRename,
  onNewChild,
  onArchive,
  pageTitle,
}: PageTreeContextMenuProps): React.ReactElement {
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>
        {children}
      </ContextMenu.Trigger>

      <ContextMenu.Portal>
        <ContextMenu.Content
          className="z-50 min-w-[160px] rounded-md border bg-popover p-1 shadow-md animate-in fade-in-0 zoom-in-95"
          aria-label={`Actions for ${pageTitle}`}
        >
          <ContextMenu.Item
            className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
            onSelect={onRename}
          >
            <span className="mr-2" aria-hidden="true">✏️</span>
            Rename
          </ContextMenu.Item>

          <ContextMenu.Item
            className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
            onSelect={onNewChild}
          >
            <span className="mr-2" aria-hidden="true">➕</span>
            New child page
          </ContextMenu.Item>

          <ContextMenu.Separator className="my-1 h-px bg-border" />

          <ContextMenu.Item
            className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none text-destructive hover:bg-destructive/10 focus:bg-destructive/10"
            onSelect={onArchive}
          >
            <span className="mr-2" aria-hidden="true">🗑️</span>
            Archive / Delete
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}
